import type {
  Lot,
  Position,
  Watchlist,
  WatchlistInfo,
} from "@foundation/watchlist-client";
import {
  ImportedData,
  ImportedSheet,
  mapDataByColumns,
  SheetColumnDef,
  SheetConfig,
} from "~utils/fileTransfer";
import type { PortfolioForm, PortfolioFormHolding } from "~types/portfolio";

export type InfoSheetData = {
  name: string;
  currency: string;
};

const infoColumns: SheetColumnDef<InfoSheetData>[] = [
  { key: "name", header: "Name" },
  { key: "currency", header: "Currency" },
];

export type HoldingsSheetData = {
  ticker: string;
  tickerName: string;
  price: number;
  openPrice: number;
  previousClosePrice: number;
  volume: number;
  marketCap: number;
  high: number;
  low: number;
  high52Week: number;
  low52Week: number;
  lastUpdated: Date;
  priceChange1D: number;
  percentChange1D: number;
  totalGainValue: number;
  totalGainValueUsd: number;
  totalGainPercent: number;
  dayGainValue: number;
  dayGainValueUsd: number;
  dayGainPercent: number;
  cost: number;
  value: number;
  shares: number;
  buyDate: Date;
  buyPrice: number;
};

const holdingsColumns: SheetColumnDef<HoldingsSheetData>[] = [
  { key: "ticker", header: "Symbol" },
  { key: "tickerName", header: "Name" },
  { key: "price", header: "Price", format: "#,##0.00" },
  { key: "buyDate", header: "Buy Date" },
  { key: "shares", header: "Shares", format: "#,##0" },
  { key: "buyPrice", header: "Buy Price", format: "#,##0.00" },
  { key: "value", header: "Value", format: "#,##0.00" },
  { key: "cost", header: "Cost", format: "#,##0.00" },
  {
    key: "lastUpdated",
    header: "Last Updated",
    format: "[$-en-US]mm/dd/yy hh:mm:ss AM/PM;@",
    width: 20,
  },
  { key: "totalGainValue", header: "Total Gain", format: "#,##0.00" },
  { key: "totalGainValueUsd", header: "Total Gain (USD)", format: "#,##0.00" },
  { key: "totalGainPercent", header: "Total Gain %", format: "#,##0.00" },
  { key: "dayGainValue", header: "Day Gain", format: "#,##0.00" },
  { key: "dayGainValueUsd", header: "Day Gain (USD)", format: "#,##0.00" },
  { key: "dayGainPercent", header: "Day Gain %", format: "#,##0.00" },
  { key: "openPrice", header: "Open Price", format: "#,##0.00" },
  { key: "previousClosePrice", header: "Previous Close", format: "#,##0.00" },
  { key: "priceChange1D", header: "Change (1D)", format: "#,##0.00" },
  { key: "percentChange1D", header: "% Change (1D)", format: "#,##0.00" },
  { key: "volume", header: "Volume", format: "#,##0", width: 20 },
  { key: "marketCap", header: "Market Cap", format: "#,##0", width: 20 },
  { key: "high", header: "Day High", format: "#,##0.00" },
  { key: "low", header: "Day Low", format: "#,##0.00" },
  { key: "high52Week", header: "52-Week High", format: "#,##0.00" },
  { key: "low52Week", header: "52-Week Low", format: "#,##0.00" },
];

const getHoldingsSheetConfig = (watchlistId: number, positions: Position[]) => {
  const holdingsDataList: HoldingsSheetData[] = [];

  try {
    positions?.forEach((position: Position) => {
      position?.lots.forEach((lot: Lot) => {
        if (!position.marketData) {
          return;
        }

        const marketData = position.marketData ?? {};
        const lotMarketValue = lot.marketValue ?? {};

        const holdingsData: HoldingsSheetData = {
          buyDate: new Date(lot.shares.buydate),
          buyPrice: lot.shares.buyprice,
          dayGainPercent: lotMarketValue.todayGainLoss?.pctChange,
          dayGainValue: lotMarketValue.todayGainLoss?.value,
          dayGainValueUsd: lotMarketValue.todayGainLoss?.value,
          high: marketData.PX_HIGH as number,
          high52Week: marketData.HIGH_52WEEK as number,
          lastUpdated: new Date(marketData.LAST_UPDATE as number),
          low: marketData.PX_LOW as number,
          low52Week: marketData.LOW_52WEEK as number,
          marketCap: marketData.CUR_MKT_CAP as number,
          openPrice: marketData.PX_OPEN as number,
          percentChange1D: marketData.CHG_PCT_1D as number,
          previousClosePrice: marketData.PX_CLOSE_1D as number,
          price: marketData.PX_LAST as number,
          priceChange1D: marketData.CHG_NET_1D as number,
          shares: lot.shares.number,
          ticker: position.security.ticker,
          tickerName: position.security.LONG_COMP_NAME,
          totalGainPercent: lotMarketValue.totalGainLoss?.pctChange,
          totalGainValue: lotMarketValue.totalGainLoss?.value,
          totalGainValueUsd: lotMarketValue.totalGainLoss?.valueUsd,
          volume: marketData.PX_VOLUME as number,
          cost: lot.shares.number * lot.shares.buyprice,
          value: lot.shares.number * (marketData.PX_LAST ?? 0),
        };

        // add lot row
        holdingsDataList.push(holdingsData);
      });
    });
  } catch (error) {
    // track error in newrelic
    window?.newrelic?.noticeError(error, { watchlistId }, false);
  }

  const holdingSheetConfig: SheetConfig<HoldingsSheetData> = {
    sheetName: "holdings",
    data: holdingsDataList,
    columns: holdingsColumns,
    sort: [{ key: "ticker", direction: "asc" }],
  };

  return holdingSheetConfig;
};

const getInfoSheet = (portInfo: WatchlistInfo) => {
  const infoDataList: InfoSheetData[] = [
    {
      name: portInfo.name,
      currency: portInfo.currency,
    },
  ];

  const infoSheetConfig: SheetConfig<InfoSheetData> = {
    sheetName: "info",
    data: infoDataList,
    columns: infoColumns,
  };

  return infoSheetConfig;
};

export const getWatchlistSheet = (watchlist: Watchlist) => {
  const infoSheetConfig: SheetConfig<InfoSheetData> = getInfoSheet(
    watchlist.portInfo
  );

  const holdingSheetConfig: SheetConfig<HoldingsSheetData> =
    getHoldingsSheetConfig(watchlist.portInfo.id, watchlist.positions);

  return {
    sheets: [holdingSheetConfig, infoSheetConfig],
  };
};

const getHoldingsFromSheet = (
  sheet?: ImportedSheet
): PortfolioFormHolding[] => {
  if (!sheet) {
    return [];
  }

  const holdingData = mapDataByColumns<HoldingsSheetData>(
    sheet.data,
    holdingsColumns
  );

  const holdingsMap = new Map<string, PortfolioFormHolding>();

  holdingData.forEach((holding) => {
    let formHolding: PortfolioFormHolding;

    if (holdingsMap.has(holding.ticker)) {
      formHolding = holdingsMap.get(holding.ticker) as PortfolioFormHolding;
    } else {
      formHolding = {
        companyName: holding.tickerName,
        costBasis: holding.cost,
        lots: [],
        ticker: holding.ticker,
        totalShares: 0,
      };
    }

    formHolding.lots.push({
      lotId: -1,
      buyDate: holding.buyDate.getTime(),
      buyPrice: String(holding.buyPrice),
      shares: String(holding.shares),
    });

    formHolding.totalShares += holding.shares;
    formHolding.costBasis += holding.cost;

    holdingsMap.set(holding.ticker, formHolding);
  });

  return Array.from(holdingsMap.values());
};

const getWatchlistInfoFromSheet = (
  fileName: string,
  sheet?: ImportedSheet
): InfoSheetData => {
  const fallback: InfoSheetData = {
    name: fileName,
    currency: "USD",
  };

  if (!sheet) {
    return fallback;
  }

  const watchlistInfo = mapDataByColumns<InfoSheetData>(
    sheet.data,
    infoColumns
  );

  if (!watchlistInfo?.length) {
    return fallback;
  }

  return watchlistInfo[0];
};

export const getPortfolioFormData = (importedData: ImportedData) => {
  let holdingSheet: ImportedSheet | undefined = undefined;
  let infoSheet: ImportedSheet | undefined = undefined;

  importedData.sheets.forEach((sheet) => {
    if (sheet.sheetName === "info") {
      infoSheet = sheet;

      return;
    }

    if (sheet.sheetName === "holdings") {
      holdingSheet = sheet;

      return;
    }
  });

  const watchlistInfo = getWatchlistInfoFromSheet(
    importedData.fileName,
    infoSheet
  );

  const newPortfolio: PortfolioForm = {
    currency: watchlistInfo.currency,
    holdings: getHoldingsFromSheet(holdingSheet),
    isDefault: false,
    name: watchlistInfo.name,
  };

  return newPortfolio;
};
